import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { Plus, Edit, Trash2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { supervisorService } from '../services/supervisorService';

const SupervisorManagement = () => {
    const { t } = useTranslation('supervisor');
    const [supervisors, setSupervisors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentSupervisor, setCurrentSupervisor] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        is_active: true
    });
    const [validated, setValidated] = useState(false);

    // Fetch supervisors
    const fetchSupervisors = async () => {
        try {
            setLoading(true);
            const data = await supervisorService.getSupervisors();
            setSupervisors(data);
            setError(null);
        } catch (err) {
            setError(t('errors.load_failed') || t('errorLoadingSupervisors'));
            console.error(t('errorFetchingSupervisors'), err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSupervisors();
    }, []);

    // Handle input change
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Show add modal
    const handleShowAddModal = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            is_active: true
        });
        setValidated(false);
        setShowAddModal(true);
    };

    // Show edit modal
    const handleShowEditModal = (supervisor) => {
        setCurrentSupervisor(supervisor);
        setFormData({
            first_name: supervisor.first_name,
            last_name: supervisor.last_name,
            email: supervisor.email,
            is_active: supervisor.is_active
        });
        setValidated(false);
        setShowEditModal(true);
    };

    // Show delete modal
    const handleShowDeleteModal = (supervisor) => {
        setCurrentSupervisor(supervisor);
        setShowDeleteModal(true);
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            if (showAddModal) {
                await supervisorService.createSupervisor(formData);
                setShowAddModal(false);
            } else if (showEditModal && currentSupervisor) {
                await supervisorService.updateSupervisor(currentSupervisor.id, formData);
                setShowEditModal(false);
            }
            fetchSupervisors();
        } catch (err) {
            setError(t('errors.save_failed') || t('errorSavingSupervisor'));
            console.error(t('errorSavingSupervisor'), err);
        }
    };

    // Delete supervisor
    const handleDelete = async () => {
        try {
            if (currentSupervisor) {
                await supervisorService.deleteSupervisor(currentSupervisor.id);
                setShowDeleteModal(false);
                fetchSupervisors();
            }
        } catch (err) {
            setError(t('errors.delete_failed') || t('errorDeletingSupervisor'));
            console.error(t('errorDeletingSupervisor'), err);
        }
    };

    // Toggle supervisor status
    const handleToggleStatus = async (supervisor) => {
        try {
            await supervisorService.updateSupervisorStatus(supervisor.id, !supervisor.is_active);
            fetchSupervisors();
        } catch (err) {
            setError(t('errors.status_update_failed') || t('errorUpdatingSupervisorStatus'));
            console.error(t('errorUpdatingSupervisorStatus'), err);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>{t('title') || t('supervisorManagement')}</h1>
                    <p className="text-muted">
                        {t('subtitle') || t('manageSupervisorsForStaffAssignments')}
                    </p>
                </Col>
                <Col xs="auto" className="d-flex align-items-center">
                    <Button
                        variant="outline-secondary"
                        className="me-2"
                        onClick={fetchSupervisors}
                    >
                        <RefreshCw size={18} />
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleShowAddModal}
                    >
                        <Plus size={18} className="me-1" />
                        {t('buttons.add') || t('addSupervisor')}
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('loading')}</span>
                            </div>
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                            <tr>
                                <th>{t('table.name') || 'Name'}</th>
                                <th>{t('table.email') || t('email')}</th>
                                <th>{t('table.status') || t('status')}</th>
                                <th className="text-end">{t('table.actions') || t('actions')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {supervisors.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-3">
                                        {t('table.no_supervisors') || t('noSupervisorsFound')}
                                    </td>
                                </tr>
                            ) : (
                                supervisors.map((supervisor) => (
                                    <tr key={supervisor.id}>
                                        <td>
                                            {supervisor.last_name}, {supervisor.first_name}
                                        </td>
                                        <td>{supervisor.email}</td>
                                        <td>
                                                <span className={`badge bg-${supervisor.is_active ? 'success' : 'danger'}`}>
                                                    {supervisor.is_active ?
                                                        (t('table.active') || t('active')) :
                                                        (t('table.inactive') || t('inactive'))}
                                                </span>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleToggleStatus(supervisor)}
                                            >
                                                {supervisor.is_active ?
                                                    (t('buttons.deactivate') || t('deactivate')) :
                                                    (t('buttons.activate') || t('activate'))}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleShowEditModal(supervisor)}
                                            >
                                                <Edit size={16} />
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleShowDeleteModal(supervisor)}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modals.add.title') || t('addSupervisor')}</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.first_name') || t('firstName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.first_name_required') || t('firstNameIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.last_name') || t('lastName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.last_name_required') || t('lastNameIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.email') || t('email')}</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.email_required') || t('validEmailIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                            {t('buttons.cancel') || t('cancel')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('buttons.save') || 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modals.edit.title') || t('editSupervisor')}</Modal.Title>
                </Modal.Header>
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.first_name') || t('firstName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.first_name_required') || t('firstNameIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.last_name') || t('lastName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.last_name_required') || t('lastNameIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('form.email') || t('email')}</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('form.email_required') || t('validEmailIsRequired')}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            {t('buttons.cancel') || t('cancel')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('buttons.save') || 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('modals.delete.title') || t('deleteSupervisor')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('modals.delete.confirmation') || t('areYouSureYouWantToDeleteThisSupervisor')}{' '}
                    {currentSupervisor && (
                        <strong>{currentSupervisor.last_name}, {currentSupervisor.first_name}</strong>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        {t('buttons.cancel') || t('cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        {t('buttons.delete') || t('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SupervisorManagement;